/**
 * @file frontend/src/pages/auth/LoginPage.js
 * @module auth
 * @component LoginPage
 *
 * Login Page — Top-level page rendered by the React Router.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Lock, Chrome, Building2, Smartphone, Shield, Eye, EyeOff } from 'lucide-react';

function returnPath(location) {
  const fromLoc = location.state && location.state.from;
  if (!fromLoc || typeof fromLoc.pathname !== 'string') return '';
  const path = fromLoc.pathname;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return '';
  const search = typeof fromLoc.search === 'string' ? fromLoc.search : '';
  if (search && !search.startsWith('?')) return path;
  return path + search;
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tfaToken, setTfaToken] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [tfaMethod, setTfaMethod] = useState('totp');
  const [samlProviders, setSamlProviders] = useState([]);
  const { login, loginWithGoogle, verify2FA, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = returnPath(location) || '/dashboard';

  // Load configured SAML providers (publicly accessible endpoint)
  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${apiBase}/api/auth/saml/providers/`)
      .then(res => setSamlProviders(res.data?.providers || []))
      .catch(() => setSamlProviders([]));
  }, []);

  const handleSAMLLogin = async (provider) => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${apiBase}${provider.login_url}?next=${encodeURIComponent(from)}`);
      if (res.data?.redirect_url) {
        window.location.href = res.data.redirect_url;
      } else {
        toast.error('SAML login URL missing');
      }
    } catch (e) {
      toast.error('SAML login failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await login(email, password);

      // Check if 2FA is required
      if (response.requires_2fa) {
        setTfaToken(response.tfa_token);
        setTfaMethod(response.tfa_method || 'totp');
        setShow2FA(true);
        toast.info(response.tfa_method === 'sms'
          ? 'Verification code sent to your phone'
          : 'Open your authenticator app and enter the 6-digit code');
      } else {
        toast.success('Welcome back!');
        // Phase 6 — mandatory 2FA setup gate
        if (response.tfa_setup_required) {
          navigate('/setup-2fa', { replace: true });
          return;
        }
        const targetRoute = returnPath(location) || response.default_route || '/dashboard';
        navigate(targetRoute, { replace: true });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error;
      toast.error(typeof errorMsg === 'string' && errorMsg ? errorMsg : 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // A controlled-input state update is async and may not have flushed by
    // the time this submit fires. Read the DOM value as a fallback so the
    // submitted code matches what the field shows.
    const domCode = document.querySelector('[data-testid="tfa-code-input"]')?.value || '';
    const codeToUse = tfaCode || domCode;

    try {
      const userData = await verify2FA(tfaToken, codeToUse);
      toast.success('Welcome back!');
      if (userData?.tfa_setup_required) {
        navigate('/setup-2fa', { replace: true });
        return;
      }
      const targetRoute = returnPath(location) || userData?.default_route || '/dashboard';
      navigate(targetRoute, { replace: true });
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error;
      toast.error(typeof errorMsg === 'string' && errorMsg ? errorMsg : 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handleMicrosoftLogin = () => {
    loginWithMicrosoft();
  };

  // Show 2FA verification form
  if (show2FA) {
    return (
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="text-center lg:text-left">
          <Link to="/" className="flex items-center justify-center lg:justify-start gap-3 mb-8 hover:opacity-80 transition-opacity cursor-pointer">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
              <span className="text-white font-bold text-xl">K</span>
            </div>
            <span className="text-slate-900 text-2xl font-bold font-heading">K12 ERP</span>
          </Link>
          <h2 className="text-3xl font-bold text-slate-900 font-heading">Two-Factor Authentication</h2>
          <p className="mt-2 text-slate-600">
            {tfaMethod === 'sms'
              ? 'Enter the verification code sent to your phone'
              : 'Open your authenticator app and enter the 6-digit code'}
          </p>
        </div>

        <div className="flex justify-center py-6">
          <div className="p-4 bg-blue-100 rounded-full">
            {tfaMethod === 'sms'
              ? <Smartphone size={48} className="text-blue-600" />
              : <Shield size={48} className="text-blue-600" />}
          </div>
        </div>

        <form onSubmit={handle2FASubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="tfa-code" className="text-slate-700">Verification Code</Label>
            <Input
              id="tfa-code"
              type="text"
              placeholder="Enter 6-digit code"
              value={tfaCode}
              onChange={(e) => setTfaCode(e.target.value)}
              className="h-12 text-center text-2xl tracking-widest"
              maxLength={6}
              required
              autoFocus
              data-testid="tfa-code-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 bg-blue-600 hover:bg-blue-700"
            disabled={isLoading || tfaCode.length < 6}
            data-testid="tfa-verify-btn"
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>

          <Button data-testid="loginpage-back-to-login-btn" 
            type="button" 
            variant="ghost"
            className="w-full"
            onClick={() => { setShow2FA(false); setTfaCode(''); }}
          >
            Back to login
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center lg:text-left">
        <Link to="/" className="flex items-center justify-center lg:justify-start gap-3 mb-8 hover:opacity-80 transition-opacity cursor-pointer">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
            <span className="text-white font-bold text-xl">K</span>
          </div>
          <span className="text-slate-900 text-2xl font-bold font-heading">K12 ERP</span>
        </Link>
        <h2 className="text-3xl font-bold text-slate-900 font-heading">Welcome back</h2>
        <p className="mt-2 text-slate-600">Sign in to your account to continue</p>
      </div>

      {/* SSO Buttons */}
      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          className="w-full h-12 gap-3 text-slate-700 border-slate-300 hover:bg-slate-50"
          onClick={handleGoogleLogin}
          data-testid="google-login-btn"
        >
          <Chrome size={20} />
          Continue with Google
        </Button>

        {samlProviders.map((p) => (
          <Button
            key={p.slug}
            type="button"
            variant="outline"
            className="w-full h-12 gap-3 text-slate-700 border-slate-300 hover:bg-slate-50"
            onClick={() => handleSAMLLogin(p)}
            data-testid={`saml-login-btn-${p.slug}`}
          >
            <Shield size={20} />
            Continue with {p.name}
          </Button>
        ))}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-slate-50 px-4 text-slate-500">or continue with email</span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700">Email address</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              id="email"
              type="email"
              placeholder="you@district.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12"
              required
              data-testid="email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-slate-700">Password</Label>
            <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 h-12"
              required
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <Button 
          type="submit" 
          className="w-full h-12 bg-blue-600 hover:bg-blue-700"
          disabled={isLoading}
          data-testid="login-submit-btn"
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
};

export default LoginPage;